import { type RefObject, useEffect, useState } from 'react';

import { gsap } from '@/lib/gsap';

export interface UseInViewOptions {
  /**
   * Fraction of the viewport height below which the element's top must fall to
   * count as "in view". 0.9 ≈ reveal once the element's top passes 90% of the
   * viewport (a touch before it is fully on-screen). Default 0.85.
   */
  threshold?: number;
  /** Stop watching after the first reveal (default true). */
  once?: boolean;
  /**
   * Skip entirely and report `true` immediately (used for reduced motion so
   * content is shown, never hidden).
   */
  disabled?: boolean;
}

/**
 * Bulletproof "is this element in view?" hook for scroll reveals.
 *
 * The visibility test is a **rect check** (`getBoundingClientRect`, always
 * viewport-relative and correct). It is evaluated from three complementary
 * sources so it can never be stranded:
 *   1. **Directly** on mount and on `scroll`/`resize` events (NOT wrapped in
 *      `requestAnimationFrame`) — scroll events fire even when rAF is throttled
 *      (e.g. a background tab), so a section is never left blank.
 *   2. On every **`gsap.ticker`** frame while focused — catches programmatic
 *      jumps and fast flicks that fire few scroll events, and elements inside a
 *      `transform-style: preserve-3d` ancestor (a `TiltCard`), which an
 *      IntersectionObserver never reports.
 * Whichever fires first reveals the element once, then all watchers detach.
 *
 * Returns `true` immediately under reduced motion so content is never hidden.
 */
export function useInView(
  ref: RefObject<HTMLElement | null>,
  { threshold = 0.85, once = true, disabled = false }: UseInViewOptions = {},
): boolean {
  const [inView, setInView] = useState(disabled);

  useEffect(() => {
    if (disabled) {
      setInView(true);
      return;
    }

    let stopped = false;

    const check = () => {
      if (stopped) return;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const isIn = rect.top < vh * threshold && rect.bottom > 0;
      if (isIn) {
        setInView(true);
        if (once) stop();
      } else if (!once) {
        setInView(false);
      }
    };

    const stop = () => {
      if (stopped) return;
      stopped = true;
      gsap.ticker.remove(check);
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };

    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    gsap.ticker.add(check);
    check();

    return stop;
  }, [ref, threshold, once, disabled]);

  return inView;
}
